import type { ResidenceCallScriptSaveBody, ResidenceCallScriptSaveResponse } from "./callScriptResidence.savePayload";
import type { ResidenceCallScriptEntry, ResidenceCallScriptHistory } from "./callScriptResidence.types";

// ---------------------------------------------------------------------------
// callScriptResidence.mock
//
// Stand-in backend for the residence ("Customer Verification") Call Script
// until the BFF ships the real endpoints (see callScriptResidence.types.ts).
//
// Backed by a module-level in-memory store keyed by approval + applicant, so
// the full loop works end to end: save appends an entry, the mutation
// invalidates the history key, the refetch shows the new call. Cleared on page
// reload — nothing is persisted.
//
// SWAPPING IN THE REAL ENDPOINTS: these two functions are the only place the
// mock lives. Replace the body of each with the generated client call (see
// the TODO in the hook that consumes it) and delete this file; nothing else
// in the feature needs to change.
// ---------------------------------------------------------------------------

/** Simulated network latency, so loading states are actually exercised in the UI. */
const MOCK_LATENCY_MS = 300;

/** Simulated `analystName` on a saved entry — the real endpoint resolves this server-side from the caller's identity. */
const MOCK_ANALYST_NAME = "PGALICIA";

/**
 * Builds the in-memory store's composite key.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @returns {string} The `approvalId::applicantId` key.
 */
function storeKey(approvalId: string, applicantId: string): string {
  return `${approvalId}::${applicantId}`;
}

/**
 * Seeded history, keyed by `approvalId::applicantId`. A key with no entry
 * yields an empty history — the modal's "No call history yet." state.
 */
const historyStore = new Map<string, ResidenceCallScriptEntry[]>();

/** Monotonic id source for entries and answers created by {@link saveResidenceCallScriptMock}. */
let nextPk = 9_000_001;

/**
 * Resolves after the simulated latency, rejecting early if the caller aborts
 * (TanStack Query passes its `signal` through on cancellation).
 * @param {AbortSignal | undefined} signal - The query's abort signal, if any.
 * @returns {Promise<void>} Resolves once the delay elapses.
 */
function delay(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new Error("Aborted"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, MOCK_LATENCY_MS);
    /** Cancels the pending timer and rejects, so an aborted query doesn't resolve late. */
    function onAbort() {
      clearTimeout(timer);
      reject(signal?.reason ?? new Error("Aborted"));
    }
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Mock stand-in for `GET .../residence-verification/call-script/history`.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {AbortSignal} [signal] - The query's abort signal.
 * @returns {Promise<ResidenceCallScriptHistory>} The applicant's saved calls, newest first.
 */
export async function fetchResidenceCallScriptHistoryMock(
  approvalId: string,
  applicantId: string,
  signal?: AbortSignal
): Promise<ResidenceCallScriptHistory> {
  await delay(signal);
  const entries = historyStore.get(storeKey(approvalId, applicantId)) ?? [];
  // Newest first, matching how the employment history endpoint returns.
  return { entries: [...entries].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)) };
}

/**
 * Mock stand-in for `POST .../residence-verification/call-script`. Appends the
 * call to the in-memory store so the post-save history refetch reflects it.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {ResidenceCallScriptSaveBody} body - The built save payload.
 * @returns {Promise<ResidenceCallScriptSaveResponse>} The persisted identifiers.
 */
export async function saveResidenceCallScriptMock(
  approvalId: string,
  applicantId: string,
  body: ResidenceCallScriptSaveBody
): Promise<ResidenceCallScriptSaveResponse> {
  await delay();
  const occurredAt = new Date().toISOString();
  const verificationPhoneCallPk = nextPk++;
  // The real endpoint records every question; the history panel only has room
  // for the ones the analyst actually answered, so drop the blanks here rather
  // than rendering a wall of empty rows.
  const answers = body.verificationAnswers
    .filter((answer) => answer.answerEntered !== "")
    .map((answer) => ({ ...answer, verificationAnswerPk: nextPk++ }));

  const entry: ResidenceCallScriptEntry = {
    verificationPhoneCallPk,
    phoneNumberDialed: body.phoneNumberDialed,
    occurredAt,
    analystName: MOCK_ANALYST_NAME,
    applicantAddressPk: body.applicantAddressPk,
    answers,
  };

  const key = storeKey(approvalId, applicantId);
  historyStore.set(key, [...(historyStore.get(key) ?? []), entry]);

  return {
    verificationPhoneCallPk,
    verificationAnswerPks: answers.map((answer) => answer.verificationAnswerPk),
    occurredAt,
  };
}

/** Empties the in-memory store. Test-only — production code never calls this. */
export function resetResidenceCallScriptMock(): void {
  historyStore.clear();
  nextPk = 9_000_001;
}
